/**
 * Sentry error tracking for Expo app.
 * Matches backend + frontend pattern: CPR scrubbing, 10% traces, no PII.
 */
import * as Sentry from '@sentry/react-native'
import { Platform } from 'react-native'

const SENTRY_DSN = process.env.EXPO_PUBLIC_SENTRY_DSN || ''
const CPR_PATTERN = /\d{6}-?\d{4}/g

function scrubCpr(event: Sentry.Event): Sentry.Event {
  const str = JSON.stringify(event)
  CPR_PATTERN.lastIndex = 0
  if (CPR_PATTERN.test(str)) {
    CPR_PATTERN.lastIndex = 0
    return JSON.parse(str.replace(CPR_PATTERN, '[CPR_REDACTED]'))
  }
  return event
}

function getEnvironment(): string {
  const railwayEnv = process.env.EXPO_PUBLIC_RAILWAY_ENV
  if (railwayEnv) return railwayEnv
  if (__DEV__) return 'local'
  return 'production'
}

export function initSentry(): void {
  if (!SENTRY_DSN) {
    if (__DEV__) console.log('[Sentry] Disabled — no EXPO_PUBLIC_SENTRY_DSN')
    return
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: getEnvironment(),
    release: `app@1.0.0`,
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
    beforeSend(event) {
      return scrubCpr(event)
    },
    enableNative: Platform.OS !== 'web',
  })

  if (__DEV__) {
    console.log(`[Sentry] Initialized (env=${getEnvironment()}, platform=${Platform.OS})`)
  }
}

export function setSentryUser(userId: string): void {
  Sentry.setUser({ id: userId })
}

export function clearSentryUser(): void {
  Sentry.setUser(null)
}

export function sentryCaptureException(
  error: Error | unknown,
  context?: Record<string, any>,
): void {
  if (context) {
    Sentry.withScope((scope) => {
      scope.setExtras(context)
      Sentry.captureException(error)
    })
  } else {
    Sentry.captureException(error)
  }
}

export { Sentry }
